import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Home, MessageCircle, BookOpen, Headphones, Star, LogOut, ChevronDown, Wind, Settings, Shield, MessageSquareHeart, User } from 'lucide-react';

const NAV_ITEMS = [
  { path: '/', label: 'Home', icon: Home, exact: true },
  { path: '/chat', label: 'Coach', icon: MessageCircle },
  { path: '/journal', label: 'Journal', icon: BookOpen },
  { path: '/meditations', label: 'Meditations', icon: Headphones },
  { path: '/breathwork', label: 'Breathwork', icon: Wind },
  { path: '/echo-map', label: 'Signs', icon: Star },
];

// Mobile bottom-nav adds a "You" link to Profile (no sidebar dropdown on mobile).
const MOBILE_NAV_ITEMS = [
  ...NAV_ITEMS,
  { path: '/profile', label: 'You', icon: User },
];

export const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className="hidden lg:flex fixed left-0 top-0 h-full w-64 flex-col bg-bone/60 backdrop-blur-2xl border-r border-gold/10 z-40"
        style={{ boxShadow: '4px 0 40px rgba(198, 178, 143, 0.06)' }}
        data-testid="desktop-sidebar"
      >
        {/* Logo */}
        <div className="p-8 pb-6">
          <img
            src="/wordmark.png"
            alt="Live in Vibrancy"
            data-testid="sidebar-wordmark"
            className="w-44 h-auto select-none"
            draggable={false}
          />
          <p className="text-gold font-sans text-[10px] tracking-[0.2em] uppercase mt-2 leading-snug">Inner Work to reveal the powerful Identity already within you ✨</p>
          <div className="mystical-divider mt-4" />
        </div>

        {/* Nav Links */}
        <nav className="flex-1 px-4 space-y-1">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`sidebar-link w-full flex items-center gap-3 px-4 py-3 text-left font-sans text-sm ${
                  active ? 'active text-dark font-medium' : 'text-text-muted'
                }`}
              >
                <Icon size={18} className={active ? 'text-gold' : 'text-text-muted'} />
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* User */}
        <div className="p-4 border-t border-black/8">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-sand/60"
                style={{ transition: 'background-color 0.2s ease' }}
                data-testid="user-menu-trigger"
              >
                <Avatar className="h-9 w-9">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-gold/20 text-dark font-sans text-xs">{initials}</AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left min-w-0">
                  <p className="font-sans text-sm text-dark truncate">{user?.name}</p>
                  <p className="font-sans text-xs text-text-muted truncate">{user?.email}</p>
                </div>
                <ChevronDown size={14} className="text-text-muted shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="w-56 bg-bone border-black/10 rounded-xl">
              <DropdownMenuItem
                onClick={() => navigate('/profile')}
                className="font-sans text-sm"
                data-testid="profile-link"
              >
                <Settings size={14} className="mr-2" /> Settings
              </DropdownMenuItem>
              {user?.is_admin && (
                <DropdownMenuItem
                  onClick={() => navigate('/admin')}
                  className="font-sans text-sm"
                  data-testid="admin-link"
                >
                  <Shield size={14} className="mr-2" /> Admin Dashboard
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => window.dispatchEvent(new Event('open-feedback'))}
                className="font-sans text-sm"
                data-testid="open-feedback-link"
              >
                <MessageSquareHeart size={14} className="mr-2" /> Send Feedback
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-red-500 focus:text-red-600 font-sans text-sm"
                data-testid="logout-button"
              >
                <LogOut size={14} className="mr-2" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile Bottom Nav */}
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 h-18 bg-bone/95 backdrop-blur-xl border-t border-black/8 z-40 flex items-center justify-around px-1 pb-safe"
        data-testid="mobile-nav"
      >
        {MOBILE_NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = isActive(item);
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              className={`flex flex-col items-center gap-1 py-2 px-2 rounded-xl ${
                active ? 'text-dark' : 'text-text-muted'
              }`}
              style={{ transition: 'color 0.2s ease' }}
            >
              <Icon size={18} className={active ? 'text-gold' : ''} />
              <span className="font-sans text-[10px] tracking-wider">{item.label}</span>
            </button>
          );
        })}
      </nav>
    </>
  );
};
